import React from "react";
import { Link } from "react-router-dom";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { PageSEO } from "@/components/seo/PageSEO";
import { SITE_URL } from "@/utils/seo";
import { ArrowRight, Truck, MapPin, Clock } from "lucide-react";

const SITE = SITE_URL;

const strollers = [
  { slug: "baby-jogger-city-mini-gt2-single", name: "Baby Jogger City Mini GT2 Single", price: 22, type: "Single" },
  { slug: "zoe-v2-single", name: "Zoe V2 Single", price: 20, type: "Single" },
  { slug: "baby-jogger-city-mini-gt2-double", name: "Baby Jogger City Mini GT2 Double", price: 34, type: "Double" },
  { slug: "zoe-v2-twin", name: "Zoe V2 Twin", price: 30, type: "Double" },
];

const faqs = [
  {
    q: "Do you deliver strollers to Universal Orlando hotels?",
    a: "Yes. We deliver to Universal resort hotels and on-site properties in the Universal Orlando area. Delivery and pickup arrangements vary by hotel — contact us when booking to confirm the logistics for your specific property.",
  },
  {
    q: "Can I take your strollers into Universal Studios and Islands of Adventure?",
    a: "Universal Studios Florida and Islands of Adventure have their own stroller size and usage policies. Strollers are generally allowed in the parks and there are designated stroller parking areas. You will need to fold the stroller for certain attractions and indoor queues.",
  },
  {
    q: "Is delivery free to Universal Orlando hotels?",
    a: "Standard delivery is free. We offer 1-hour delivery windows from 7 am to 10 pm — no early-delivery surcharges.",
  },
  {
    q: "Can I book the stroller for Universal and then extend to Walt Disney World?",
    a: "Yes. You can book stroller rental dates that span multiple parks and locations. Select your full rental period when reserving and include all delivery locations.",
  },
  {
    q: "What is the lightest stroller you offer for Universal?",
    a: "The Zoe V2 Single is our lightest single stroller at approximately 14 lbs. It is easy to fold and carry, which is helpful when navigating Universal's queues and indoor attractions.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Strollers", item: `${SITE}/strollers` },
    { "@type": "ListItem", position: 3, name: "Universal Orlando Stroller Rentals", item: `${SITE}/universal-orlando-stroller-rentals` },
  ],
};

export default function UniversalOrlandoStrollers() {
  return (
    <>
      <PageSEO
        title="Universal Orlando Stroller Rentals | Free Hotel Delivery"
        description="Rent a premium stroller for your Universal Orlando vacation. Free delivery to Universal resort hotels and area properties. Baby Jogger, Zoe and more from $20/day."
        canonical={`${SITE}/universal-orlando-stroller-rentals`}
        structuredData={[breadcrumbSchema, faqSchema]}
      />
      <Nav />
      <main className="min-h-screen" style={{ background: "#040A0F" }}>
        <section className="pt-28 pb-16 text-center" style={{ background: "linear-gradient(180deg, #060f18 0%, #040A0F 100%)" }}>
          <div className="container max-w-3xl">
            <nav aria-label="Breadcrumb" className="text-xs text-[#B9C8D5] mb-4">
              <Link to="/" className="hover:text-[#FFD166]">Home</Link>
              <span className="mx-2 opacity-40">/</span>
              <Link to="/strollers" className="hover:text-[#FFD166]">Strollers</Link>
              <span className="mx-2 opacity-40">/</span>
              <span className="text-[#FFD166]">Universal Orlando Strollers</span>
            </nav>
            <h1 className="font-serif text-4xl md:text-5xl text-[#F6F1E4] mb-5">Stroller Rentals for Universal Orlando</h1>
            <p className="text-[#B9C8D5] text-lg max-w-2xl mx-auto">
              Premium strollers delivered to Universal Orlando resort hotels and area properties. Enchanted Gear Rentals is not affiliated with Universal Parks &amp; Resorts. We are an independent family-owned rental service serving the entire Orlando area.
            </p>
            <div className="mt-7 flex gap-4 justify-center flex-wrap">
              <Link to="/#trip-setup"><button className="btn-primary" data-testid="universal-cta-top">Check Availability <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
              <Link to="/disney-world-stroller-rentals"><button className="btn-ghost">Also visiting Disney World?</button></Link>
            </div>
          </div>
        </section>

        {/* Service info */}
        <section className="container py-12">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-6">Universal Orlando stroller delivery</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {[
              { Icon: MapPin, title: "Universal resort hotels", body: "We deliver to Universal's on-site resort hotels. Contact us to confirm arrangements for your specific property." },
              { Icon: Truck, title: "Free standard delivery", body: "No delivery charge for any window. Delivery is always free." },
              { Icon: Clock, title: "Delivery windows", body: "1-hour windows available 7 am – 10 pm. Pick the exact hour that works for your schedule." },
            ].map(({ Icon, title, body }) => (
              <div key={title} className="rounded-xl bg-[#0A1825] border border-[#FFD166]/15 p-6">
                <Icon className="w-6 h-6 text-[#FFD166] mb-3" />
                <h3 className="font-serif text-lg text-[#F6F1E4] mb-2">{title}</h3>
                <p className="text-[#B9C8D5] text-sm leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Stroller tips for Universal */}
        <section className="container py-10">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-6">Stroller tips for Universal Studios and Islands of Adventure</h2>
          <div className="rounded-xl bg-[#0A1825] border border-[#FFD166]/15 p-6 max-w-3xl">
            <ul className="space-y-3 text-sm text-[#B9C8D5]">
              <li>Universal has designated stroller parking areas at park entrances and near major attractions.</li>
              <li>You will need to fold the stroller for some indoor attractions and certain queues.</li>
              <li>A lightweight, compact stroller is ideal for navigating Universal's indoor areas.</li>
              <li>Universal's parks allow strollers for children under a certain height — check Universal's current policy for the latest guidance.</li>
              <li>Our single strollers are lighter and easier to fold than doubles, which can be an advantage at Universal.</li>
            </ul>
          </div>
        </section>

        {/* Products */}
        <section className="container py-10">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-4">Popular strollers for Universal Orlando</h2>
          <p className="text-[#B9C8D5] mb-8">Compact, lightweight options that navigate Universal's parks and resort hotels easily.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {strollers.map((s) => (
              <Link key={s.slug} to={`/strollers/${s.slug}`} className="group rounded-xl bg-[#0A1825] border border-[#FFD166]/10 hover:border-[#FFD166]/40 p-5 transition-all hover:-translate-y-1">
                <div className="text-[10px] uppercase tracking-widest text-[#B9C8D5] border border-[#FFD166]/20 px-2 py-0.5 rounded-full w-fit mb-3">{s.type}</div>
                <div className="font-serif text-[#F6F1E4] group-hover:text-[#FFD166] transition-colors mb-2">{s.name}</div>
                <div className="text-[#FFD166] font-semibold">From ${s.price}/day</div>
              </Link>
            ))}
          </div>
          <div className="mt-5"><Link to="/strollers" className="text-sm text-[#FFD166] underline">View full stroller catalog</Link></div>
        </section>

        {/* FAQ */}
        <section className="container py-10">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-8">Universal Orlando stroller FAQ</h2>
          <div className="space-y-4 max-w-3xl">
            {faqs.map((f) => (
              <details key={f.q} className="group rounded-xl bg-[#0A1825] border border-[#FFD166]/10 p-5">
                <summary className="font-semibold text-[#F6F1E4] cursor-pointer list-none flex justify-between items-center gap-3">
                  {f.q}
                  <span className="text-[#FFD166] text-xl leading-none shrink-0 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-[#B9C8D5] text-sm leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="container pb-20 text-center">
          <div className="max-w-xl mx-auto rounded-2xl bg-[#0A1825] border border-[#FFD166]/20 p-8">
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-3">Book your Universal Orlando stroller</h2>
            <p className="text-[#B9C8D5] mb-6">Check availability and reserve in minutes. Questions? We are happy to help via phone or text at 689-345-RENT.</p>
            <Link to="/#trip-setup"><button className="btn-primary" data-testid="universal-cta-bottom">Check Availability <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
